import { useState, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  RefreshCw,
  SlidersHorizontal,
  DownloadCloud,
  LayoutList,
  Clock,
  Pizza,
  MoreVertical,
  SearchX,
  CheckCheck,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { useOrders, Order } from "@/hooks/useOrders";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import OrderFilters from "@/components/OrderFilters";
import OrderSorting, { SortField, SortDirection } from "@/components/OrderSorting";
import { ViewOfferDialog } from "@/components/ViewOfferDialog";
import { useNotificationSettings } from "@/hooks/useNotificationSettings";
import { PendingOrdersToggle } from "@/components/PendingOrdersToggle";
import { DeliveryOrdersToggle } from "@/components/DeliveryOrdersToggle";
import { TimeGroupedOrdersView } from "@/components/TimeGroupedOrdersView";
import OrderCard from "@/components/OrderCard";
import { OrderCardSkeleton } from "@/components/OrderCardSkeleton";
import { EmptyState } from "@/components/EmptyState";
import { useSendGuard } from "@/hooks/useSendGuard";
import type { OrderActionKey } from "@/utils/orderActions";

type ViewMode = "list" | "grouped";

/** One in-flight slot per order + lifecycle step. */
const sendKey = (order: Order, action: OrderActionKey) => `${order.order_id}:${action}`;

const SORT_FIELD_LABEL: Record<SortField, string> = {
  order_no: "order number",
  promise_time: "promise time",
};

const SORT_DIRECTION_LABEL: Record<SortField, Record<SortDirection, string>> = {
  order_no: { desc: "newest first", asc: "oldest first" },
  promise_time: { asc: "earliest first", desc: "latest first" },
};

const Dashboard = () => {
  const [selectedOrderType, setSelectedOrderType] = useState("All");
  const [sortField, setSortField] = useState<SortField>("order_no");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [showFilters, setShowFilters] = useState(false);
  const [isViewOfferDialogOpen, setIsViewOfferDialogOpen] = useState(false);
  const [selectedOfferOrder, setSelectedOfferOrder] = useState<Order | null>(null);
  const [isFetchingOrders, setIsFetchingOrders] = useState(false);
  const [showPendingOnly, setShowPendingOnly] = useState(true);
  const [showDeliveryOrders, setShowDeliveryOrders] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("list");

  const {
    orders,
    loading,
    error,
    isRefreshing,
    updateOrderConfirmation,
    updateOrderReady,
    manualRefresh,
    updateReviewRequestSent,
  } = useOrders({ sortField, sortDirection, showPendingOnly, showDeliveryOrders });

  const { toast } = useToast();
  const { settings: notificationSettings } = useNotificationSettings();
  const getSupabaseClient = useSupabaseClient();

  // Every SMS send goes through this one gate. It is owned here, above the order
  // rows, so it outlives a card unmounting mid-send (List <-> By time, a
  // collapsed time window, a filter that drops the row) and a fresh card can
  // never offer a send that is already on its way to the customer.
  const { isSending, runGuarded } = useSendGuard();
  const isOrderSending = useCallback(
    (order: Order, action: OrderActionKey) => isSending(sendKey(order, action)),
    [isSending],
  );

  const handleSendReviewRequest = useCallback((order: Order) =>
    runGuarded(sendKey(order, "review"), async () => {
      if (updateReviewRequestSent) {
        await updateReviewRequestSent(order, true);
      } else {
        console.error("updateReviewRequestSent function is not available from useOrders hook.");
        toast({
          title: "Error",
          description: "Could not send review request. Function unavailable.",
          variant: "destructive",
        });
      }
    }), [runGuarded, updateReviewRequestSent, toast]);

  const handleSendConfirmation = useCallback((order: Order) =>
    runGuarded(sendKey(order, "confirm"), async () => {
      const success = await updateOrderConfirmation(order, true);
      if (success) {
        toast({
          title: "Confirmation Sent",
          description: `Order confirmation SMS has been sent for order #${order.order_no}.`,
          className: "bg-status-success text-status-success-foreground",
        });
      }
    }), [runGuarded, updateOrderConfirmation, toast]);

  const handleFetchOrders = useCallback(async () => {
    setIsFetchingOrders(true);
    try {
      const supabase = await getSupabaseClient();
      if (!supabase) {
        throw new Error("Not authenticated.");
      }

      // The POS-relay Lambda's shared secret lives server-side in the
      // fetch-orders edge function's env, not in this bundle.
      const { data, error } = await supabase.functions.invoke("fetch-orders", {
        method: "POST",
      });

      if (!error) {
        toast({
          title: "Fetch Successful",
          description: "New orders have been fetched.",
          className: "bg-status-success text-status-success-foreground",
        });
        manualRefresh();
      } else {
        toast({
          title: "Fetch Failed",
          description: `Error: ${data?.error || error.message}`,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Fetch Error",
        description: "An unexpected error occurred while fetching orders.",
        variant: "destructive",
      });
      console.error("Fetch error:", error);
    } finally {
      setIsFetchingOrders(false);
    }
  }, [getSupabaseClient, manualRefresh, toast]);

  const handleSendReadyNotification = useCallback((order: Order) =>
    runGuarded(sendKey(order, "ready"), async () => {
      const success = await updateOrderReady(order, true);
      if (success) {
        toast({
          title: "Ready Notification Sent",
          description: `Order ready notification SMS has been sent for order #${order.order_no}.`,
          className: "bg-status-success text-status-success-foreground",
        });
      }
    }), [runGuarded, updateOrderReady, toast]);

  const handleViewOffer = useCallback((order: Order) => {
    if (order.offer_id && order.order_id) {
      setSelectedOfferOrder(order);
      setIsViewOfferDialogOpen(true);
    }
  }, []);

  // Orders arrive pre-filtered (today, pending/delivery, sorted) from the hook;
  // this only applies the client-side order type filter.
  const filteredOrders = useMemo(() => {
    return orders.filter(
      (order) =>
        selectedOrderType === "All" ||
        (order.order_type && order.order_type.toLowerCase().includes(selectedOrderType.toLowerCase())),
    );
  }, [orders, selectedOrderType]);

  // The Delivery toggle widens the query rather than narrowing it, so it is a
  // view preference, not a filter: it never counts here and Clear never resets it.
  const narrowingActive = selectedOrderType !== "All";
  const activeFilterCount = narrowingActive ? 1 : 0;

  const clearFilters = useCallback(() => {
    setSelectedOrderType("All");
  }, []);

  // Hiding delivery rows would leave a Delivery narrowing pointed at nothing, so
  // the filter falls back to All rather than silently emptying the queue.
  const handleDeliveryToggle = useCallback((value: boolean) => {
    setShowDeliveryOrders(value);
    if (!value) {
      setSelectedOrderType((current) => (current === "Delivery" ? "All" : current));
    }
  }, []);

  const getTodayDate = () =>
    new Date().toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const orderCountLabel = `${filteredOrders.length} ${filteredOrders.length === 1 ? "order" : "orders"}`;
  // By-time always orders by pickup window, so the sort choice only describes
  // the list view; advertising it in grouped mode would describe nothing.
  const isListView = viewMode === "list";
  const sortLabel = isListView
    ? `${SORT_FIELD_LABEL[sortField]}, ${SORT_DIRECTION_LABEL[sortField][sortDirection]}`
    : null;
  // By-time groups on promise_time and drops orders without one, so the count
  // line has to say what the grouped view is not showing.
  const untimedCount = isListView ? 0 : filteredOrders.filter((o) => !o.promise_time).length;

  if (loading) {
    return (
      <div className="pb-6">
        <div className="mb-6 sm:mb-8 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-2">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-40" />
            </div>
            <Skeleton className="h-11 w-11 rounded-md" />
          </div>
          <div className="flex items-center justify-between gap-2">
            <Skeleton className="h-11 w-44 rounded-lg" />
            <Skeleton className="h-11 w-28 rounded-md" />
          </div>
        </div>
        <div className="space-y-4 sm:space-y-6">
          {[1, 2, 3].map((i) => (
            <OrderCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full">
        <div className="bg-status-danger/10 border border-status-danger/30 rounded-lg p-6">
          <h3 className="text-status-danger font-medium text-lg">Error loading orders</h3>
          <p className="text-status-danger text-sm mt-2">{error}</p>
          {error.includes("Authentication error") && (
            <div className="mt-4 p-3 bg-status-caution/10 border border-status-caution/30 rounded">
              <p className="text-status-caution-foreground text-sm">
                <strong>Authentication Issue:</strong> The app is having trouble connecting to the database.
                This usually resolves automatically within a few seconds. If the problem persists, please refresh the page.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pb-6">
      {/* Header: title + a single actions menu, then a compact toolbar. */}
      <div className="mb-6 sm:mb-8 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Today's Orders</h1>
            <p className="mt-1 text-sm text-muted-foreground">{getTodayDate()}</p>
          </div>

          {/* Refresh / Fetch: inline on desktop, collapsed into one menu below sm. */}
          <div className="flex-none">
            <div className="hidden sm:flex items-center gap-2">
              <Button
                onClick={manualRefresh}
                disabled={isRefreshing}
                variant="outline"
                className="h-11 gap-2 px-4 text-sm font-medium"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
                <span>{isRefreshing ? "Refreshing" : "Refresh"}</span>
              </Button>
              <Button
                onClick={handleFetchOrders}
                disabled={isFetchingOrders}
                variant="outline"
                className="h-11 gap-2 px-4 text-sm font-medium"
              >
                <DownloadCloud className={`h-4 w-4 ${isFetchingOrders ? "animate-pulse" : ""}`} />
                <span>{isFetchingOrders ? "Fetching" : "Fetch orders"}</span>
              </Button>
            </div>
            <div className="sm:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="h-11 w-11" aria-label="Order actions">
                    <MoreVertical className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={manualRefresh} disabled={isRefreshing}>
                    <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
                    <span>Refresh</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleFetchOrders} disabled={isFetchingOrders}>
                    <DownloadCloud className={`mr-2 h-4 w-4 ${isFetchingOrders ? "animate-pulse" : ""}`} />
                    <span>Fetch orders</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Toolbar: view mode + filters. One row, comfortable at 360px. */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center rounded-lg border border-border bg-muted p-0.5">
            <Button
              onClick={() => setViewMode("list")}
              variant="ghost"
              className={`h-11 gap-1.5 rounded-md px-3 text-sm font-medium ${
                viewMode === "list"
                  ? "bg-background text-foreground shadow-sm hover:bg-background"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              aria-pressed={viewMode === "list"}
            >
              <LayoutList className="h-4 w-4" />
              <span>List</span>
            </Button>
            <Button
              onClick={() => setViewMode("grouped")}
              variant="ghost"
              className={`h-11 gap-1.5 rounded-md px-3 text-sm font-medium ${
                viewMode === "grouped"
                  ? "bg-background text-foreground shadow-sm hover:bg-background"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              aria-pressed={viewMode === "grouped"}
            >
              <Clock className="h-4 w-4" />
              <span>By time</span>
            </Button>
          </div>

          <Button
            onClick={() => setShowFilters((v) => !v)}
            variant="outline"
            className={`h-11 gap-2 px-4 text-sm font-medium ${
              showFilters ? "border-action-primary/40 bg-action-primary/[0.06] text-action-primary" : ""
            }`}
            aria-expanded={showFilters}
          >
            <SlidersHorizontal className="h-4 w-4" />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-action-primary px-1.5 text-xs font-semibold text-action-primary-foreground tabular-nums">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>

        {/* Count summary sits right above the queue it describes. */}
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground tabular-nums">{orderCountLabel}</span>
          {showPendingOnly ? " · pending only" : " · all orders"}
          {sortLabel && ` · ${sortLabel}`}
          {untimedCount > 0 && ` · ${untimedCount} without a pickup time`}
        </p>
      </div>

      {/* Collapsible controls: show toggles, then filter, then sort. */}
      {showFilters && (
        <div className="mb-6 rounded-radius-card border border-border bg-card p-4 sm:p-5 space-y-5">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Show</h3>
              {activeFilterCount > 0 && (
                <Button
                  onClick={clearFilters}
                  variant="ghost"
                  className="h-11 px-3 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  Clear filters
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <PendingOrdersToggle
                showPendingOnly={showPendingOnly}
                onToggle={setShowPendingOnly}
                pendingCount={orders.filter((o) => !o.is_posted).length}
                totalCount={orders.length}
              />
              <DeliveryOrdersToggle
                showDeliveryOrders={showDeliveryOrders}
                onToggle={handleDeliveryToggle}
                deliveryCount={orders.filter((o) => o.order_type?.toLowerCase().includes("delivery")).length}
              />
            </div>
          </div>

          <div className="border-t border-border pt-5">
            <OrderFilters
              selectedOrderType={selectedOrderType}
              onOrderTypeChange={setSelectedOrderType}
              includeDelivery={showDeliveryOrders}
            />
          </div>

          {/* Sort only drives the list view; By-time is always ordered by pickup window. */}
          {isListView && (
            <div className="border-t border-border pt-5">
              <OrderSorting
                sortField={sortField}
                sortDirection={sortDirection}
                onSortFieldChange={setSortField}
                onSortDirectionChange={setSortDirection}
              />
            </div>
          )}
        </div>
      )}

      {/* Orders. Empty states are resolved before the view mode, so By-time gets
          the same no-match recovery path as List. */}
      {filteredOrders.length > 0 ? (
        viewMode === "grouped" ? (
          <TimeGroupedOrdersView
            orders={filteredOrders}
            onSendConfirmation={handleSendConfirmation}
            onSendReadyNotification={handleSendReadyNotification}
            onViewOffer={handleViewOffer}
            onSendReviewRequest={handleSendReviewRequest}
            notificationSettings={notificationSettings}
            isSending={isOrderSending}
          />
        ) : (
          <div className="space-y-4 sm:space-y-6">
            {filteredOrders.map((order) => (
              <OrderCard
                key={order.order_id}
                order={order}
                onSendConfirmation={handleSendConfirmation}
                onSendReadyNotification={handleSendReadyNotification}
                onViewOffer={handleViewOffer}
                onSendReviewRequest={handleSendReviewRequest}
                notificationSettings={notificationSettings}
                isSending={(action) => isOrderSending(order, action)}
              />
            ))}
          </div>
        )
      ) : narrowingActive ? (
        /* A filter is narrowing the queue, so the way out comes first: without
           this the recovery action disappears exactly when the filter is what
           emptied the view. */
        <EmptyState
          icon={SearchX}
          title="No orders match these filters"
          description={
            orders.length > 0 ? (
              <>
                {orders.length} {orders.length === 1 ? "order is" : "orders are"} loaded. Clear the filters to see them.
              </>
            ) : (
              "No orders match the current filters. Clear them to widen the view."
            )
          }
        >
          <Button
            onClick={clearFilters}
            className="h-11 gap-2 bg-action-primary text-action-primary-foreground hover:opacity-90"
          >
            Clear filters
          </Button>
        </EmptyState>
      ) : showPendingOnly ? (
        /* Pending-only queries out the finished orders, so an empty result cannot
           tell "everything is posted" from "nothing came in yet". Claim neither;
           offer the toggle that reveals completed orders and the register pull. */
        <EmptyState
          icon={CheckCheck}
          title="No pending orders"
          description="Pending orders show up here as they come in. Show all orders to include completed ones, or fetch the latest from the register."
          accent="primary"
        >
          <Button
            onClick={() => setShowPendingOnly(false)}
            className="h-11 w-full gap-2 bg-action-primary text-action-primary-foreground hover:opacity-90 sm:w-auto"
          >
            Show all orders
          </Button>
          <Button
            onClick={handleFetchOrders}
            disabled={isFetchingOrders}
            variant="outline"
            className="h-11 w-full gap-2 sm:w-auto"
          >
            <DownloadCloud className={`h-4 w-4 ${isFetchingOrders ? "animate-pulse" : ""}`} />
            {isFetchingOrders ? "Fetching" : "Fetch orders"}
          </Button>
          <Button
            onClick={manualRefresh}
            disabled={isRefreshing}
            variant="outline"
            className="h-11 w-full gap-2 sm:w-auto"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
            {isRefreshing ? "Refreshing" : "Refresh"}
          </Button>
        </EmptyState>
      ) : (
        /* Genuinely quiet: nothing in the queue right now. */
        <EmptyState
          icon={Pizza}
          title="Waiting for orders"
          description="New orders land here the moment they come in. Pull the latest from the register any time."
          accent="primary"
        >
          <Button
            onClick={handleFetchOrders}
            disabled={isFetchingOrders}
            className="h-11 w-full gap-2 bg-action-primary text-action-primary-foreground hover:opacity-90 sm:w-auto"
          >
            <DownloadCloud className={`h-4 w-4 ${isFetchingOrders ? "animate-pulse" : ""}`} />
            {isFetchingOrders ? "Fetching" : "Fetch orders"}
          </Button>
          <Button
            onClick={manualRefresh}
            disabled={isRefreshing}
            variant="outline"
            className="h-11 w-full gap-2 sm:w-auto"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
            {isRefreshing ? "Refreshing" : "Refresh"}
          </Button>
        </EmptyState>
      )}

      <ViewOfferDialog
        isOpen={isViewOfferDialogOpen}
        onClose={() => setIsViewOfferDialogOpen(false)}
        order={selectedOfferOrder}
      />
    </div>
  );
};

export default Dashboard;
